import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Stack,
  Typography,
  IconButton,
  Box,
  Container,
  Divider,
  Link,
  Chip,
  CircularProgress,
  List,
  ListItemText,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import Iconify from '@/components/Iconify';
import { fDecimalCurrency } from '@/utils/formatNumber';
import TicketInsuranceDetail from './TicketInsuranceDetail';
import MsciDetails from '@/sections/msi/MsciDetail';
import RenderIf from '@/components/RenderIf';

function OrderDetails({
  title,
  resume: {
    ticketsDetail,
    subtotalTickets,
    totalProcessorFee,
    totalServiceFee,
    totalTransactionFee,
    total,
    currency,
    discount,
    promotion,
    ticketInsurance,
    commissions,
  },
  hideTicketInsurance = false,
  installments,
  hideHeader = false,
  hideTotal = false,
  onRemovePromotion,
  onAddPromotion,
  disabledTicketInsuranceAction = false,
  enableSubtotalCalc = false,
  showTextCoupon = false,
  isSmall = false,
}) {
  const { t } = useTranslation();
  const [stillWaiting, setStillWaiting] = useState(true);

  const { preferences: { fee_breakdown = 't' } = {} } = useSelector(
    (state) => state.event || {},
  );

  useEffect(() => {
    if (promotion && discount <= 0) {
      setStillWaiting(true);
      setTimeout(() => {
        setStillWaiting(false);
      }, 1500);
    }
  }, [promotion]);

  return (
    <Stack spacing={isSmall ? 1 : 2}>
      {!hideHeader && (
        <Stack
          direction="row"
          sx={{
            width: '100%',
            height: '56px',
            alignItems: 'center',
          }}
        >
          <Box sx={{ minWidth: 80 }}>
            <Typography
              sx={{
                fontSize: isSmall ? '14px' : '19px',
                fontWeight: 600,
              }}
            >
              {title || t('checkout.global.yourTickets')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}></Box>
          <Typography
            sx={{
              fontSize: isSmall ? '12px' : '14px',
              color: 'text.secondary',
            }}
          >
            Divisa {currency}
          </Typography>
        </Stack>
      )}
      <Stack spacing={1}>
        {ticketsDetail?.map((item) => (
          <Stack key={item.id}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                {`${item.name} (${item.qty})`}
              </Typography>
              <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                {fDecimalCurrency(item.price * item.qty)}
              </Typography>
            </Stack>
            <RenderIf condition={item?.zones?.length > 0}>
              <List sx={{ color: 'text.secondary', padding: 0 }}>
                {item.zones.reverse().map((zone) => (
                  <ListItemText key={zone} sx={{ pl: 2 }}>
                    <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                      <CircleIcon
                        sx={{ fontSize: '6px', mr: 1, color: 'text.disabled' }}
                      />
                      {zone}
                    </Typography>
                  </ListItemText>
                ))}
              </List>
            </RenderIf>
          </Stack>
        ))}
        {promotion && (
          <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
            <Box sx={{ minWidth: 80 }}>
              <Typography variant="subtitle2">
                <RenderIf condition={showTextCoupon}>
                  <Typography
                    Component="span"
                    sx={{ fontSize: '14px', fontWeight: 400 }}
                  >
                    {`Cupón ${promotion}`}
                  </Typography>
                </RenderIf>
                <RenderIf condition={!showTextCoupon}>
                  <Chip
                    label={`Cupón ${promotion}`}
                    size="small"
                    icon={<Iconify width={14} height={14} icon={'mdi:tag'} />}
                    color="success"
                    onDelete={onRemovePromotion ? onRemovePromotion : null}
                    sx={{
                      'color': '#fff',
                      'background': '#139e65',
                      '& .MuiChip-deleteIcon': { color: '#fff' },
                    }}
                  />
                </RenderIf>
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box>
              {discount <= 0 && stillWaiting && (
                <CircularProgress size={24} color="success" />
              )}
              {discount > 0 && (
                <Typography
                  sx={{
                    fontWeight: 400,
                    color: '#139e65',
                    fontSize: isSmall ? '12px' : '14px',
                  }}
                >
                  {`- ${fDecimalCurrency(discount)}`}
                </Typography>
              )}
              {discount <= 0 && !stillWaiting && (
                <Typography
                  sx={{
                    color: '#139e65',
                    fontSize: isSmall ? '12px' : '14px',
                  }}
                >
                  {t('checkout.global.noPromo')}
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" justifyContent="center" alignItems="center">
        <Divider sx={{ width: '100%' }} />
      </Stack>
      <Stack
        direction="row"
        sx={{ width: '100%', mt: 1, alignItems: 'center' }}
      >
        <Box sx={{ minWidth: 80 }}>
          <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
            {t('checkout.global.subtotal')}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }}></Box>
        <Box>
          <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
            {`${fDecimalCurrency(
              enableSubtotalCalc ? subtotalTickets - discount : subtotalTickets,
            )} `}
          </Typography>
        </Box>
      </Stack>

      {!promotion && !!onAddPromotion && discount <= 0 && (
        <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
          <Box sx={{ minWidth: 80 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: (theme) => theme.palette.secondary.main,
              }}
            >
              {t('checkout.coupons.haveCoupon')}
              <Iconify width={14} height={14} icon={'mdi:tag'} />
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}></Box>
          <Box>
            <Link
              onClick={onAddPromotion}
              variant="body2"
              sx={{ textDecoration: 'underline' }}
            >
              {t('checkout.buttons.apply')}
            </Link>
          </Box>
        </Stack>
      )}

      {commissions ? (
        <>
          <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
            <Box sx={{ minWidth: 80 }}>
              <Typography variant="body3">
                {t('checkout.global.charge')}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: '36px',
                }}
              >
                {`${fDecimalCurrency(commissions.boletia_commission)}`}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
            <Box sx={{ minWidth: 80 }}>
              <Typography variant="body3">
                {t('checkout.global.paymentProcessor')}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: '36px',
                }}
              >
                {`${fDecimalCurrency(commissions.payment_method_commission)}`}
              </Typography>
            </Box>
          </Stack>
        </>
      ) : (
        <>
          {' '}
          {fee_breakdown === 't' && (
            <Stack gap={1}>
              <RenderIf condition={totalServiceFee > 0}>
                <Stack
                  direction="row"
                  sx={{ width: '100%', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 80 }}>
                    <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                      {t('checkout.global.charge')}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}></Box>
                  <Box>
                    <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                      {`${fDecimalCurrency(totalServiceFee)}`}
                    </Typography>
                  </Box>
                </Stack>
              </RenderIf>
              <RenderIf condition={totalTransactionFee > 0}>
                <Stack
                  direction="row"
                  sx={{ width: '100%', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 80 }}>
                    <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                      {t('checkout.global.ticketFee')}{' '}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}></Box>
                  <Box>
                    <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                      {`${fDecimalCurrency(totalTransactionFee)} `}
                    </Typography>
                  </Box>
                </Stack>
              </RenderIf>
              <RenderIf condition={totalProcessorFee > 0}>
                <Stack
                  direction="row"
                  sx={{ width: '100%', alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 80 }}>
                    <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                      {t('checkout.global.paymentMethodCharge')}{' '}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}></Box>
                  <Box>
                    <Typography sx={{ fontSize: isSmall ? '12px' : '14px' }}>
                      {`${fDecimalCurrency(totalProcessorFee)}`}
                    </Typography>
                  </Box>
                </Stack>
              </RenderIf>
            </Stack>
          )}
        </>
      )}
      <RenderIf condition={!hideTicketInsurance}>
        <TicketInsuranceDetail
          ticketInsurance={ticketInsurance}
          disabledTicketInsuranceAction={disabledTicketInsuranceAction}
        />
      </RenderIf>

      {!hideTotal && (
        <>
          <Stack
            sx={{ mt: 2, mb: 2 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Divider sx={{ width: '100%' }} />
          </Stack>
          <Stack>
            <Stack direction="row" sx={{ width: '100%', alignItems: 'center' }}>
              <Box sx={{ minWidth: 80 }}>
                <Typography variant="h5">
                  {t('checkout.global.total')}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}></Box>
              <Box>
                <Typography variant="h5">{`${fDecimalCurrency(
                  total,
                )} ${currency}`}</Typography>
              </Box>
            </Stack>
            {installments?.payment_per_month > 0 &&
              installments?.payment_type !== 'flat payment' && (
                <MsciDetails installments={installments} currency={currency} />
              )}
          </Stack>
        </>
      )}
    </Stack>
  );
}

OrderDetails.propTypes = {
  resume: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onRemovePromotion: PropTypes.func,
  onAddPromotion: PropTypes.func,
  EventCheckoutDetails: PropTypes.string,
  hideAction: PropTypes.bool,
};

export default OrderDetails;
